// Stories group data. Everything here is assembled from src/data/*.js, the legacy
// page components in src/App.jsx and design/wireframe-2026-09/CONTENT.md.
// Nothing is invented: when a fact is missing, the pages leave it out.

import { AAROH_IMAGES } from "../../data/images.js";
import { MEDIA_LIBRARY } from "../../data/seo.js";
import { peopleMet, stories } from "../../data/content.js";
import { AAROH_ALBUM, AAROH_CLIPS, AAROH_REEL } from "../../data/album.js";

export const AAROH_ALBUM_URL = "https://photos.app.goo.gl/ipECCr2Dz9jkjKfM9";
export const AAROH_DATES = "1 to 2 August 2026";

const ALBUM_BY_ID = Object.fromEntries(AAROH_ALBUM.map((item) => [item.id, item]));
const CLIP_BY_ID = Object.fromEntries(AAROH_CLIPS.map((item) => [item.id, item]));

// One album photo as an article figure. `size` picks the 1600 or 800 file.
function albumFigure(id, extra = {}) {
  const item = ALBUM_BY_ID[id];
  return { type: "figure", src: extra.small ? item.srcSmall : item.src, width: item.width, height: item.height, alt: item.alt, caption: item.caption, ...extra };
}

function albumPair(a, b) {
  return { type: "pair", items: [albumFigure(a, { small: true }), albumFigure(b, { small: true })] };
}

export function albumPhoto(id) {
  return ALBUM_BY_ID[id];
}

export function albumClip(id) {
  return CLIP_BY_ID[id];
}

export { AAROH_REEL };

/* Story articles. Body copy comes from CONTENT.md and the legacy StoryPage. */

export const STORY_ARTICLES = {
  "what-is-project-grid": {
    kind: "Field note",
    lead: {
      src: "/images/grid-photo-06-p7.jpg",
      alt: "Moksh Sindhwani and Tanishq Aggarwal together at Metamorphosis",
      caption: "GRID co-founders Moksh Sindhwani and Tanishq Aggarwal.",
    },
    standfirst: "GRID is a student-led initiative for young people who want to work on ideas with others.",
    sections: [
      {
        id: "where-it-began",
        title: "Where it began",
        blocks: [
          {
            type: "p",
            text: "Moksh Sindhwani and Tanishq Aggarwal founded GRID after attending the Young Changemakers Bootcamp at IIT Delhi, run by Tale of Humankind Foundation. Their aim was to create more opportunities for students to try building something of their own.",
          },
          {
            type: "p",
            text: "They started GRID in December 2025. A team of 18 school students now runs programmes, technology, partnerships and community.",
          },
          {
            type: "p",
            text: "Change starts when young people are given the tools, trust and room to build. GRID creates shared spaces where questions can become prototypes, conversations and initiatives.",
          },
          {
            type: "quote",
            text: "Give ambitious students a real problem, a supportive network and a path to keep going.",
          },
        ],
      },
      {
        id: "in-practice",
        title: "What that means in practice",
        blocks: [
          {
            type: "p",
            text: "GRID's work so far spans AAROH, Aurora and outreach for Metamorphosis. The programmes have different organisers and formats, so each account explains GRID's role separately.",
          },
          albumFigure("M054", { wide: true }),
          {
            type: "p",
            text: "AAROH was a two-day student innovation festival at IIIT Delhi on 1 and 2 August 2026, organised by GRID with Tachyon India and CiPD, IIIT Delhi. 363 young innovators took part and thirteen teams were recognised.",
          },
          albumPair("M087", "M609"),
          {
            type: "p",
            text: "Aurora is a Project GRID programme: a global online hackathon for students in Grades 8-12, built so students with limited resources and slow internet could still take part. 1,500 participants took part, from 27+ countries.",
          },
          {
            type: "figure",
            src: "/images/grid-photo-01-p1.jpg",
            alt: "Students gathered at the Metamorphosis programme",
            caption: "Metamorphosis. GRID was the outreach partner.",
          },
          {
            type: "p",
            text: "Metamorphosis is led by Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives. GRID was the outreach partner and helped bring students in. 150 students from 18 states took part.",
          },
          {
            type: "links",
            items: [
              ["AAROH: event recap", "/programmes/aaroh"],
              ["Aurora: programme results", "/programmes/aurora"],
              ["Metamorphosis: outreach partnership", "/programmes/metamorphosis"],
            ],
          },
        ],
      },
      {
        id: "starting-point",
        title: "Your starting point",
        blocks: [
          {
            type: "p",
            text: "Join an announced programme when applications are open, contact the team about contributing, or use a guide to begin a project independently.",
          },
          {
            type: "links",
            items: [
              ["Find your next step", "/for-students"],
              ["Upcoming events", "/programmes/upcoming"],
              ["Project guides", "/guides"],
            ],
          },
        ],
      },
    ],
  },
  "learning-by-building": {
    kind: "Field note",
    lead: {
      src: AAROH_IMAGES.buildSession,
      alt: "Students and a mentor working together around laptops at AAROH",
      caption: "A working session at AAROH.",
    },
    standfirst: "A prototype is useful when it helps you answer a question.",
    sections: [
      {
        id: "real-difficulty",
        title: "Begin with a real difficulty",
        blocks: [
          {
            type: "p",
            text: "An idea becomes easier to assess when you know who needs it and what they do now. Talk to people before deciding which tool to build. Record what they say separately from your interpretation.",
          },
          {
            type: "pair",
            items: [
              {
                type: "figure",
                src: AAROH_IMAGES.studentSpeaker,
                alt: "A student speaking with a microphone during AAROH at IIIT Delhi",
                caption: "Student voices enter the room. AAROH, IIIT Delhi.",
              },
              albumFigure("M167", { small: true }),
            ],
          },
        ],
      },
      {
        id: "small-enough",
        title: "Make something small enough to test",
        blocks: [
          {
            type: "quote",
            text: "Talk to people before deciding which tool to build.",
          },
          {
            type: "p",
            text: "A paper sketch, a simple webpage or a physical model can help someone respond to an idea. Choose the format that answers your next question with the least work.",
          },
          albumFigure("M102"),
        ],
      },
      {
        id: "watch",
        title: "Watch what happens",
        blocks: [
          {
            type: "p",
            text: "Ask someone to try a task. Notice where they hesitate and what they misunderstand. Explain the prototype's limits and ask permission before recording anything.",
          },
          { type: "video", ...AAROH_CLIPS.find((clip) => clip.id === "M029"), wide: true },
          albumPair("M580", "M104"),
        ],
      },
      {
        id: "use-it",
        title: "Use what you learn",
        blocks: [
          {
            type: "p",
            text: "Keep the parts that help. Change the parts that fail. A useful project account explains both, including what the team still needs to test.",
          },
          albumFigure("M682", { wide: true }),
          {
            type: "links",
            items: [
              ["Make a first prototype", "/guides/idea-to-prototype"],
              ["All project guides", "/guides"],
            ],
          },
        ],
      },
    ],
  },
};

function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function readingMinutes(slug) {
  const article = STORY_ARTICLES[slug];
  if (!article) return 1;
  let words = countWords(article.standfirst);
  article.sections.forEach((section) => {
    words += countWords(section.title);
    section.blocks.forEach((block) => {
      if (block.type === "p") words += countWords(block.text);
      if (block.type === "figure" || block.type === "video") words += countWords(block.caption);
      if (block.type === "pair") block.items.forEach((item) => { words += countWords(item.caption); });
    });
  });
  return Math.max(1, Math.round(words / 200));
}

export const STORY_LIST = stories.map((story) => ({
  ...story,
  ...STORY_ARTICLES[story.slug],
  minutes: readingMinutes(story.slug),
}));

/* Photo library. The first eight entries are MEDIA_LIBRARY, unchanged. The rest use
   captions already published on the homepage, in content.js or in CONTENT.md. */

const MEDIA_PROGRAMME = {
  community: "AAROH",
  founders: "GRID team",
  makerspace: "Metamorphosis",
  speaker: "AAROH",
  cohort: "AAROH",
  "aurora-winners": "Aurora",
  builders: "AAROH",
  partnerships: "AAROH",
};

const EXTRA_PHOTOS = [
  {
    id: "metamorphosis-cohort",
    fallback: "/images/grid-photo-01-p1.jpg",
    width: 1800,
    height: 1202,
    alt: "Students gathered at the Metamorphosis programme",
    caption: "Metamorphosis. GRID was the outreach partner.",
    href: "/programmes/metamorphosis",
    linkLabel: "Read about GRID's outreach role",
    programme: "Metamorphosis",
  },
  {
    id: "aaroh-recognition",
    fallback: AAROH_IMAGES.recognition,
    width: 1800,
    height: 1012,
    alt: "An AAROH student team receiving recognition on stage",
    caption: "A student team receiving recognition at AAROH 2026.",
    href: "/programmes/aaroh",
    linkLabel: "Read the AAROH recap",
    programme: "AAROH",
  },
  {
    id: "aurora-overall-winner",
    fallback: "/images/grid-photo-10-p8.jpg",
    width: 960,
    height: 1280,
    alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his laptop prize",
    caption: "Overall winner Jerovin Floyd with his prize laptop.",
    href: "/programmes/aurora",
    linkLabel: "See the Aurora programme",
    programme: "Aurora",
  },
  {
    id: "metamorphosis-lhc",
    fallback: "/images/grid-photo-07-p7.jpg",
    width: 1800,
    height: 1202,
    // The file has a white band along the bottom; the tile crops to the group.
    tileHeight: 960,
    alt: "A large student cohort assembled outside the Lecture Hall Complex at IIT Delhi",
    caption: "Metamorphosis students outside the Lecture Hall Complex, IIT Delhi.",
    href: "/programmes/metamorphosis",
    linkLabel: "Read about GRID's outreach role",
    programme: "Metamorphosis",
  },
  {
    id: "aaroh-presentation",
    fallback: AAROH_IMAGES.studentPresentation,
    width: 1800,
    height: 1200,
    alt: "A student presenting from a lectern to the AAROH auditorium",
    caption: "A student taking the room through his work.",
    href: "/programmes/aaroh",
    linkLabel: "Read the AAROH recap",
    programme: "AAROH",
  },
  {
    id: "co-founders",
    fallback: "/images/grid-photo-06-p7.jpg",
    width: 1800,
    height: 1202,
    alt: "Moksh Sindhwani and Tanishq Aggarwal together at Metamorphosis",
    caption: "GRID co-founders Moksh Sindhwani and Tanishq Aggarwal.",
    href: "/team",
    linkLabel: "Meet the Project GRID team",
    programme: "GRID team",
  },
  {
    id: "metamorphosis-closing",
    fallback: "/images/grid-photo-02-p7.jpg",
    width: 1800,
    height: 1202,
    alt: "A speaker on stage at the Metamorphosis closing session",
    caption: "The Metamorphosis closing session at IIT Delhi.",
    href: "/programmes/metamorphosis",
    linkLabel: "Read about GRID's outreach role",
    programme: "Metamorphosis",
  },
  {
    id: "aurora-best-innovation",
    fallback: "/images/grid-photo-11-p8.jpg",
    width: 960,
    height: 1280,
    alt: "Gantavya Rohilla holding his Aurora prize laptop",
    caption: "Gantavya with his Aurora prize laptop.",
    href: "/programmes/aurora",
    linkLabel: "See the Aurora programme",
    programme: "Aurora",
  },
  {
    id: "aaroh-discussion",
    fallback: AAROH_IMAGES.studentDiscussion,
    width: 1800,
    height: 1200,
    alt: "Students speaking during a discussion at AAROH",
    caption: "Students speaking during a discussion at AAROH.",
    href: "/programmes/aaroh",
    linkLabel: "Read the AAROH recap",
    programme: "AAROH",
  },
];

// Album categories for the AAROH photos that were already on the site.
const EXISTING_AAROH_CATEGORY = {
  community: "crowd",
  speaker: "presenting",
  cohort: "crowd",
  builders: "building",
  partnerships: "awards",
  "aaroh-recognition": "awards",
  "aaroh-presentation": "presenting",
  "aaroh-discussion": "presenting",
};

const LIBRARY = MEDIA_LIBRARY.map((item) => ({
  ...item,
  programme: MEDIA_PROGRAMME[item.id] ?? "AAROH",
  artwork: item.id === "aurora-winners",
}));

// Interleave the published library with the extra photographs so "All" mixes programmes.
export const PHOTOS = LIBRARY.flatMap((item, index) => (EXTRA_PHOTOS[index] ? [item, EXTRA_PHOTOS[index]] : [item])).concat(
  EXTRA_PHOTOS.slice(LIBRARY.length),
);

/* The full library: the 17 entries above, the 70-photo AAROH album and 7 AAROH clips.
   Every item has kind ("photo" | "video"), programme, an optional AAROH category,
   grid (small file for the grid), full (file for the lightbox), width, height, alt, caption, href, linkLabel. */

const EXISTING = PHOTOS.map((item) => ({
  ...item,
  kind: "photo",
  category: item.programme === "AAROH" ? EXISTING_AAROH_CATEGORY[item.id] : undefined,
  grid: item.fallback,
  full: item.fallback,
}));

const ALBUM = [...AAROH_ALBUM]
  .sort((a, b) => a.rank - b.rank)
  .map((item) => ({
    id: `album-${item.id.toLowerCase()}`,
    kind: "photo",
    programme: "AAROH",
    category: item.category,
    grid: item.srcSmall,
    full: item.src,
    width: item.width,
    height: item.height,
    alt: item.alt,
    caption: item.caption,
    href: "/programmes/aaroh",
    linkLabel: "Read the AAROH recap",
    album: true,
  }));

const CLIPS = AAROH_CLIPS.map((clip) => ({
  id: `clip-${clip.id.toLowerCase()}`,
  kind: "video",
  programme: "AAROH",
  category: clip.category,
  video: clip.src,
  grid: clip.poster,
  full: clip.src,
  poster: clip.poster,
  duration: clip.duration,
  width: clip.width,
  height: clip.height,
  alt: clip.alt,
  caption: clip.caption,
  href: "/programmes/aaroh",
  linkLabel: "Read the AAROH recap",
  album: true,
}));

// Order for "All": the twelve strongest album photos alternate with the existing library,
// then the rest of the album. A clip lands every eleven items so video shows up early.
function buildLibrary() {
  const heroes = ALBUM.slice(0, 12);
  const rest = ALBUM.slice(12);
  const merged = [];
  const longest = Math.max(heroes.length, EXISTING.length);
  for (let index = 0; index < longest; index += 1) {
    if (heroes[index]) merged.push(heroes[index]);
    if (EXISTING[index]) merged.push(EXISTING[index]);
  }
  merged.push(...rest);
  const out = [];
  let clip = 0;
  merged.forEach((item, index) => {
    if (index % 11 === 3 && CLIPS[clip]) out.push(CLIPS[clip++]);
    out.push(item);
  });
  return out.concat(CLIPS.slice(clip));
}

export const MEDIA = buildLibrary();
export const MEDIA_COUNTS = {
  photos: MEDIA.filter((item) => item.kind === "photo").length,
  clips: MEDIA.filter((item) => item.kind === "video").length,
  aaroh: MEDIA.filter((item) => item.programme === "AAROH").length,
};

export const PHOTO_FILTERS = ["All", "AAROH", "Metamorphosis", "Aurora", "GRID team"];

export const AAROH_SUBFILTERS = [
  ["all", "All AAROH"],
  ["video", "Clips"],
  ["crowd", "The hall"],
  ["building", "Building"],
  ["presenting", "Presenting"],
  ["awards", "Awards"],
  ["teams", "Teams"],
  ["candid", "Candid"],
  ["venue", "Venue"],
  ["details", "Details"],
];

export const FILTER_NOTES = {
  All: "Every photograph and clip on this page, with its caption and the page that explains it.",
  AAROH: "AAROH at IIIT Delhi, 1 to 2 August 2026, organised by GRID with Tachyon India and CiPD. Photographs and silent clips from the event album.",
  Metamorphosis: "Metamorphosis at IIT Delhi was led by Tale of Humankind Foundation and IIT Delhi's outreach office. GRID was the outreach partner.",
  Aurora: "Aurora is a Project GRID programme. The campaign artwork is marked as artwork, not as a photograph.",
  "GRID team": "The students who run GRID.",
};

/* Meeting records. peopleMet has name, role, context, image, sourceUrl and date.
   Place and attendees are filled only where another source on the site states them. */

const RECORD_EXTRAS = {
  "ajai-chowdhry": {
    alt: "Ajai Chowdhry standing with two students in a room",

    place: null,
    attendees: "Ajai Chowdhry and members of the GRID team",
    status: "Meeting only",
    statusNote: "A conversation. Not an endorsement, partnership or role in GRID.",
    next: null,
  },
  "ministry-innovation-cell": {
    alt: "A large lightbulb sculpture in a garden, photographed during the visit",

    place: "AICTE and the Ministry of Education's Innovation Cell",
    attendees: "Members of the GRID team",
    status: "Visit only",
    statusNote: "A visit. Not an endorsement, partnership or institutional agreement.",
    next: null,
  },
  "sir-ganga-ram-hospital": {
    alt: "The Chairman of Sir Ganga Ram Hospital standing with two students in his office",

    place: null,
    attendees: "The Chairman of Sir Ganga Ram Hospital and members of the GRID team",
    status: "Meeting only",
    statusNote: "A discussion and presentation. Not an endorsement, partnership or role in GRID.",
    next: null,
  },
  "cbse-skill-education": {
    alt: "Students seated across a desk from an official in an office",

    place: null,
    attendees: "The Director, Skill Education at CBSE, and members of the GRID team",
    status: "Meeting only",
    statusNote: "A conversation. Not an endorsement, partnership or institutional agreement.",
    next: null,
  },
  "metamorphosis-cohort": {
    alt: "Students seated in a tiered auditorium with red seats",

    place: null,
    attendees: "150 young builders in the Metamorphosis cohort",
    status: "Programme cohort",
    statusNote:
      "Metamorphosis was led by Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives. GRID was the outreach partner.",
    next: { label: "How GRID supported Metamorphosis", to: "/programmes/metamorphosis" },
  },
};

export const MEETINGS = peopleMet.map((meeting, index) => ({
  ...meeting,
  ...RECORD_EXTRAS[meeting.slug],
  number: index + 1,
}));
